import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import TitlePages from "../../layout/TitlePages";
import Breadcrumbs from "../../layout/Breadcrumbs";
import "./PreguntasFrecuentes.css";

/* Componente reutilizable para cada pregunta colapsable con "Leer más" / "Leer menos" */
const PreguntaFrecuente = ({ id, numero, pregunta, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = `panel-${id}`;

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash) {
      const currentHash = window.location.hash.replace("#", "").toLowerCase();
      if (
        currentHash === id.toLowerCase() ||
        currentHash === `pregunta-${id.toLowerCase()}` ||
        currentHash === numero.toLowerCase()
      ) {
        setIsOpen(true);
        const timer = setTimeout(() => {
          const el = document.getElementById(id);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 350);
        return () => clearTimeout(timer);
      }
    }
  }, [id, numero]);

  const toggle = () => setIsOpen((prev) => !prev);

  return (
    <article id={id} className="pf-item">
      <button
        type="button"
        className="pf-header-btn"
        onClick={toggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <span className="pf-numero" aria-hidden="true">{numero}.</span>
        <h3 className="pf-pregunta-texto">{pregunta}</h3>
      </button>

      <button
        type="button"
        className="pf-toggle-btn"
        onClick={toggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <span>{isOpen ? "Leer menos" : "Leer más"}</span>
        <svg
          className={`pf-chevron-icon ${isOpen ? "is-open" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div
        id={panelId}
        role="region"
        aria-label={`Respuesta a pregunta ${numero}`}
        className={`pf-panel ${isOpen ? "is-open" : ""}`}
      >
        <div className="pf-panel-inner">
          <div className="pf-respuesta-content">
            {children}
          </div>
        </div>
      </div>
    </article>
  );
};

PreguntaFrecuente.propTypes = {
  id: PropTypes.string.isRequired,
  numero: PropTypes.string.isRequired,
  pregunta: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

/* Botón estilo píldora elegante para enlaces */
const PillLink = ({ to, href, target = "_blank", children, isMultiline = false }) => {
  const icon = (
    <svg
      className="pf-pill-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );

  const className = `pf-pill-link ${isMultiline ? "pf-pill-link--multiline" : ""}`.trim();
  const destination = to || href;

  if (destination && destination.startsWith("/")) {
    return (
      <Link to={destination} target={target} rel="noreferrer" className={className}>
        {icon}
        <span>{children}</span>
      </Link>
    );
  }

  return (
    <a href={destination} target={target} rel="noreferrer" className={className}>
      {icon}
      <span>{children}</span>
    </a>
  );
};

PillLink.propTypes = {
  to: PropTypes.string,
  href: PropTypes.string,
  target: PropTypes.string,
  children: PropTypes.node.isRequired,
  isMultiline: PropTypes.bool,
};

const PreguntasFrecuentes = () => {
  useEffect(() => {
    document.title = `Preguntas Frecuentes`;
  }, []);

  return (
    <>
      <Breadcrumbs path={[{ label: "Preguntas Frecuentes" }]} />
      <TitlePages title="Preguntas Frecuentes" subTitle="" />

      <div className="PFrecuentes">
        {/* Pregunta I */}
        <PreguntaFrecuente
          id="pregunta-i"
          numero="I"
          pregunta="¿Dónde puedo consultar los Resultados Electorales de las Elecciones celebradas en el Estado de Tlaxcala?"
        >
          <p>
            Te informamos que el Instituto Tlaxcalteca de Elecciones, es
            responsable del ejercicio de la función estatal de preparación,
            organización, desarrollo, vigilancia y validez de los procesos de
            elección para renovar los poderes Legislativo y Ejecutivo del
            Estado, los ayuntamientos y las presidencias de comunidad (artículo
            20 de la Ley de Instituciones y Procedimientos Electorales Para el
            Estado de Tlaxcala).
          </p>
          <p>
            Consulta los resultados electorales de las elecciones de
            Gubernaturas, Diputaciones, Ayuntamientos y Presidencias de
            Comunidad en el siguiente enlace:
          </p>
          <div className="pf-links-group">
            <PillLink to="/ProcesosElectorales">
              Procesos Electorales
            </PillLink>
          </div>
        </PreguntaFrecuente>

        {/* Pregunta II */}
        <PreguntaFrecuente
          id="pregunta-ii"
          numero="II"
          pregunta="¿Dónde puedo consultar a las candidaturas del Proceso Electoral Local Extraordinario del Poder Judicial?"
        >
          <p>
            El Instituto Tlaxcalteca de Elecciones ha creado un micrositio para consulta
            de la ciudadania respecto a la Elección Judicial Local 2025, te invitamos a conocer 
            más en el siguiente enlace:
          </p>
          <div className="pf-links-group">
            <PillLink to="https://pelepj-2025.itetlax.org.mx/?route=lista-candidaturas">
              Lista de Candidaturas
            </PillLink>
          </div>
        </PreguntaFrecuente>

        {/* Pregunta III */}
        <PreguntaFrecuente
          id="pregunta-iii"
          numero="III"
          pregunta="¿Cuánto presupuesto se destinó al Proceso Electoral Local Extraordinario del Poder Judicial?"
        >
          <p>
            El Instituto Tlaxcalteca de Elecciones recibió $75,000,000.00 (Setenta y cinco millones de pesos 00/100 M.N) para el desarrollo del Proceso Electoral Local Extraordinario del Poder Judicial, lo anterior de conformidad con el Decreto N°138 del Periódico Oficial publicado el dieciocho de marzo del año dos mil veinticinco.
          </p>
          <div className="pf-links-group">
            <PillLink
              href="assets/pdf/transparencia/DECRETO_138.pdf"
              isMultiline={true}
            >
              Presupuesto del Proceso Electoral Local Extraordinario del Poder Judicial (Decreto N°138)
            </PillLink>
          </div>
        </PreguntaFrecuente>

        {/* Pregunta IV */}
        <PreguntaFrecuente
          id="pregunta-iv"
          numero="IV"
          pregunta="¿Cuál es el Programa Anual de Auditorías Internas del Órgano Interno de Control del Instituto Tlaxcalteca de Elecciones?"
        >
          <p>
            El Órgano Interno de Control cuenta con un Cronograma de Actividades de Auditoria para el ejercicio fiscal 2025, el cual esta disponible en el siguiente hipervinculo:
          </p>
          <div className="pf-links-group">
            <PillLink to="https://drive.google.com/file/d/1cc--BxPO4iHXQm3kwFZoAPxXLCvp5f_W/view?usp=sharing">
              Cronograma de Actividades
            </PillLink>
          </div>
        </PreguntaFrecuente>

        {/* Pregunta V */}
        <PreguntaFrecuente
          id="pregunta-v"
          numero="V"
          pregunta="¿Cómo puedo saber quiénes se registraron como candidatos/as a un cargo de elección en el Estado de Tlaxcala?"
        >
          <p>
            El artículo 8 fracción II de la Ley de Instituciones y
            Procedimientos Electorales para el Estado de Tlaxcala, señala que es
            un derecho poder ser votado para todos los cargos de elección
            popular y para ser nombrado para desempeñar cualquier otro empleo o
            comisión, teniendo las cualidades que establezca la Constitución
            Local las demás leyes aplicables.
          </p>
          <p>
            El derecho de solicitar el registro de candidatos ante la autoridad
            electoral corresponde a los partidos políticos, así como a los
            ciudadanos que soliciten su registro de manera independiente a
            aquéllos y cumplan con los requisitos, condiciones y términos que
            determinen las leyes aplicables.
          </p>
          <p>
            Puedes consultar los acuerdos del Consejo General del Instituto
            Tlaxcalteca de Elecciones mediante los que se aprobaron registros de
            candidaturas a diferentes cargos de elección, dando clic en el
            siguiente enlace:
          </p>
          <div className="pf-links-group">
            <PillLink to="/Acuerdos2025">
              Acuerdos 2025
            </PillLink>
          </div>
        </PreguntaFrecuente>

        {/* Pregunta VI */}
        <PreguntaFrecuente
          id="pregunta-vi"
          numero="VI"
          pregunta="¿Dónde puedo consultar la Lista Nominal y el Padrón Electoral?"
        >
          <p>
            De conformidad con lo dispuesto por los artículos 41, base V,
            apartado B, inciso a, numeral 3, de la Constitución Política de los
            Estados Unidos Mexicanos, y 32, numeral 1, inciso a), fracción III
            de la Ley General de Instituciones y Procedimientos Electorales, es
            atribución del Instituto Nacional Electoral para los procesos
            electorales federales y locales formar y administrar el padrón
            electoral y la lista nominal de electores. Por lo que puedes
            solicitar esta información a dicho Órgano Electoral.
          </p>
          <p>
            Puedes presentar tu solicitud dirigida al Instituto Nacional
            Electoral accediendo al siguiente enlace:
          </p>
          <div className="pf-links-group">
            <PillLink href="https://listanominal.ine.mx/scpln/">
              Lista nominal y padrón electoral
            </PillLink>
          </div>
        </PreguntaFrecuente>

        {/* Pregunta VII */}
        <PreguntaFrecuente
          id="pregunta-vii"
          numero="VII"
          pregunta="¿Cuál es el presupuesto otorgado al Instituto Tlaxcalteca de Elecciones?"
        >
          <p>
            Te informamos que al Instituto Tlaxcalteca de Elecciones le es
            aprobado un presupuesto cada ejercicio fiscal, por parte del
            Congreso del Estado, mediante el cual cumple con sus funciones, gran
            parte de este recuso se otorga a los partidos políticos.
          </p>
          <p>
            Te invitamos a que conozcas el presupuesto otorgado a nuestro
            Instituto:
          </p>

          <div className="TablaWrap">
            <table className="pf-table">
              <thead>
                <tr>
                  <th scope="col" className="pf-table-col-text">Ejercicio</th>
                  <th scope="col" className="text-end pf-col-num">Presupuesto otorgado</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="pf-table-col-text">2021</td>
                  <td className="pf-table-col-monto">$185,782,415.00</td>
                </tr>
                <tr>
                  <td className="pf-table-col-text">2022</td>
                  <td className="pf-table-col-monto">$106,128,665.79</td>
                </tr>
                <tr>
                  <td className="pf-table-col-text">2023</td>
                  <td className="pf-table-col-monto">$14,351,216.00</td>
                </tr>
                <tr>
                  <td className="pf-table-col-text">2024</td>
                  <td className="pf-table-col-monto">$14,351,216.00</td>
                </tr>
                <tr>
                  <td className="pf-table-col-text">2025</td>
                  <td className="pf-table-col-monto">$128,806,150.00</td>
                </tr>
                <tr>
                  <td className="pf-table-col-text">
                    2025 AMPLIACIÓN PARA EL PROCESO ELECTORAL LOCAL EXTRAORDINARIO DEL PODER JUDICIAL
                  </td>
                  <td className="pf-table-col-monto">$75,000,000.00</td>
                </tr>
                <tr>
                  <td className="pf-table-col-text">2026</td>
                  <td className="pf-table-col-monto">$134,990,553.00</td>
                </tr>
              </tbody>
            </table>
          </div>
        </PreguntaFrecuente>

        {/* Pregunta VIII */}
        <PreguntaFrecuente
          id="pregunta-viii"
          numero="VIII"
          pregunta="¿Cuál es el monto de las Prerrogativas otorgadas a los Partidos Políticos?"
        >
          <p>
            Con fundamento en el artículo 76, fracción IV, de la Ley de
            Instituciones y Procedimientos Electorales Para el Estado de
            Tlaxcala, el Instituto Tlaxcalteca de Elecciones a través de la
            Dirección de Prerrogativas, Administración y Fiscalización es el
            encargado de otorgar las prerrogativas a los Partidos Políticos.
            Estas prerrogativas se desprenden del recurso otorgado al Instituto
            Tlaxcalteca de Elecciones.
          </p>
          <p>
            Consulta las prerrogativas que el Instituto Tlaxcalteca de
            Elecciones ha otorgado a los partidos políticos:
          </p>

          <div className="TablaWrap">
            <table className="pf-table">
              <thead>
                <tr>
                  <th rowSpan="2" className="pf-table-col-text pf-col-sticky">PARTIDO POLÍTICO</th>
                  <th colSpan="6" className="text-center">EJERCICIO</th>
                </tr>
                <tr className="pf-table-subhead">
                  <th className="pf-col-num">2021</th>
                  <th className="pf-col-num">2022</th>
                  <th className="pf-col-num">2023</th>
                  <th className="pf-col-num">2024</th>
                  <th className="pf-col-num">2025</th>
                  <th className="pf-col-num">2026</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row" className="pf-table-col-text pf-col-sticky">Partido Acción Nacional</th>
                  <td className="pf-table-col-monto">$9,224,124.08</td>
                  <td className="pf-table-col-monto">$6,182,254.86</td>
                  <td className="pf-table-col-monto">$57,287,747.00</td>
                  <td className="pf-table-col-monto">$10,520,093.80</td>
                  <td className="pf-table-col-monto">$6,815,044.00</td>
                  <td className="pf-table-col-monto">$7,244,381.00</td>
                </tr>
                <tr>
                  <th scope="row" className="pf-table-col-text pf-col-sticky">Partido Revolucionario Institucional</th>
                  <td className="pf-table-col-monto">$4,832,677.55</td>
                  <td className="pf-table-col-monto">$7,924,120.58</td>
                  <td className="pf-table-col-monto">$9,341,087.00</td>
                  <td className="pf-table-col-monto">$13,483,122.10</td>
                  <td className="pf-table-col-monto">$6,844,200.00</td>
                  <td className="pf-table-col-monto">$7,275,373.00</td>
                </tr>
                <tr>
                  <th scope="row" className="pf-table-col-text pf-col-sticky">Partido de la Revolución Democrática</th>
                  <td className="pf-table-col-monto">$5,454,360.93</td>
                  <td className="pf-table-col-monto">$4,150,946.11</td>
                  <td className="pf-table-col-monto">$4,893,206.00</td>
                  <td className="pf-table-col-monto">$7,064,703.81</td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                </tr>
                <tr>
                  <th scope="row" className="pf-table-col-text pf-col-sticky">Partido de la Revolución Democrática Tlaxcala</th>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto">$4,839,978.00</td>
                  <td className="pf-table-col-monto">$5,144,889.00</td>
                </tr>
                <tr>
                  <th scope="row" className="pf-table-col-text pf-col-sticky">Partido del Trabajo</th>
                  <td className="pf-table-col-monto">$7,317,919.98</td>
                  <td className="pf-table-col-monto">$5,288,732.35</td>
                  <td className="pf-table-col-monto">$6,234,448.00</td>
                  <td className="pf-table-col-monto">$9,000,153.14</td>
                  <td className="pf-table-col-monto">$9,876,872.00</td>
                  <td className="pf-table-col-monto">$10,499,099.00</td>
                </tr>
                <tr>
                  <th scope="row" className="pf-table-col-text pf-col-sticky">Partido Verde Ecologista de México</th>
                  <td className="pf-table-col-monto">$4,826,613.79</td>
                  <td className="pf-table-col-monto">$3,180,506.22</td>
                  <td className="pf-table-col-monto">$3,749,234.00</td>
                  <td className="pf-table-col-monto">$5,413,921.63</td>
                  <td className="pf-table-col-monto">$5,684,859.00</td>
                  <td className="pf-table-col-monto">$6,042,997.00</td>
                </tr>
                <tr>
                  <th scope="row" className="pf-table-col-text pf-col-sticky">Movimiento Ciudadano</th>
                  <td className="pf-table-col-monto">$5,665,001.65</td>
                  <td className="pf-table-col-monto">$1,096,520.59</td>
                  <td className="pf-table-col-monto">$1,292,597.00</td>
                  <td className="pf-table-col-monto">$1,861,353.22</td>
                  <td className="pf-table-col-monto">$8,153,789.00</td>
                  <td className="pf-table-col-monto">$8,667,464.00</td>
                </tr>
                <tr>
                  <th scope="row" className="pf-table-col-text pf-col-sticky">Morena</th>
                  <td className="pf-table-col-monto">$25,694,956.22</td>
                  <td className="pf-table-col-monto">$13,861,256.38</td>
                  <td className="pf-table-col-monto">$16,339,884.00</td>
                  <td className="pf-table-col-monto">$,23,582,581.10</td>
                  <td className="pf-table-col-monto">$14,127,116.00</td>
                  <td className="pf-table-col-monto">$15,017,102.00</td>
                </tr>
                <tr>
                  <th scope="row" className="pf-table-col-text pf-col-sticky">Nueva Alianza</th>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                </tr>
                <tr>
                  <th scope="row" className="pf-table-col-text pf-col-sticky">Nueva Alianza Tlaxcala</th>
                  <td className="pf-table-col-monto">$4,963,793.69</td>
                  <td className="pf-table-col-monto">$3,574,472.74</td>
                  <td className="pf-table-col-monto">$4,213,626.00</td>
                  <td className="pf-table-col-monto">$6,084,050.61</td>
                  <td className="pf-table-col-monto">$4,242,762.00</td>
                  <td className="pf-table-col-monto">$4,510,049.00</td>
                </tr>
                <tr>
                  <th scope="row" className="pf-table-col-text pf-col-sticky">Encuentro Social</th>
                  <td className="pf-table-col-monto">$1,646,037.19</td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                </tr>
                <tr>
                  <th scope="row" className="pf-table-col-text pf-col-sticky">Encuentro Social Tlaxcala</th>
                  <td className="pf-table-col-monto">$4,446,188.11</td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                </tr>
                <tr>
                  <th scope="row" className="pf-table-col-text pf-col-sticky">Partido Alianza Ciudadana</th>
                  <td className="pf-table-col-monto">$1,646,037.19</td>
                  <td className="pf-table-col-monto">$4,079,095.56</td>
                  <td className="pf-table-col-monto">$4,808,506.00</td>
                  <td className="pf-table-col-monto">$6,942,481.29</td>
                  <td className="pf-table-col-monto">$6,029,091.00</td>
                  <td className="pf-table-col-monto">$6,408,913.00</td>
                </tr>
                <tr>
                  <th scope="row" className="pf-table-col-text pf-col-sticky">Partido Socialista</th>
                  <td className="pf-table-col-monto">$1,646,037.19</td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                </tr>
                <tr>
                  <th scope="row" className="pf-table-col-text pf-col-sticky">Impacto Social "Sí"</th>
                  <td className="pf-table-col-monto">$1,646,037.19</td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto"></td>
                </tr>
                <tr>
                  <th scope="row" className="pf-table-col-text pf-col-sticky">Fuerza por México Tlaxcala</th>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto">$3,817,027.52</td>
                  <td className="pf-table-col-monto">$4,499,577.00</td>
                  <td className="pf-table-col-monto">$6,496,686.30</td>
                  <td className="pf-table-col-monto">$4,817,919.00</td>
                  <td className="pf-table-col-monto">$5,121,440.00</td>
                </tr>
                <tr>
                  <th scope="row" className="pf-table-col-text pf-col-sticky">Redes Sociales Progresistas Tlaxcala</th>
                  <td className="pf-table-col-monto"></td>
                  <td className="pf-table-col-monto">$1,096,520.59</td>
                  <td className="pf-table-col-monto">$1,292,597.00</td>
                  <td className="pf-table-col-monto">$1,861,353.22</td>
                  <td className="pf-table-col-monto">$3,979,517.00</td>
                  <td className="pf-table-col-monto">$4,230,220.00</td>
                </tr>
              </tbody>
            </table>
          </div>
        </PreguntaFrecuente>

        {/* Pregunta IX */}
        <PreguntaFrecuente
          id="pregunta-ix"
          numero="IX"
          pregunta="¿Dónde puedo consultar el Registro de Representantes ante mesas directivas de casilla?"
        >
          <p>
            Te informamos que con base en los artículos 262 de la Ley General de
            Instituciones y Procedimientos Electorales, 254 del Reglamento de
            Elecciones del Instituto Nacional Electoral, el registro de
            representantes generales y ante mesas directivas de casilla, de
            partidos políticos y candidaturas independientes, en cualquier
            proceso electoral federal o local, sean estos ordinarios o
            extraordinarios, está a cargo del Instituto Nacional Electoral a
            través de sus Consejos Locales y Distritales.
          </p>
          <p>
            Puedes presentar tu solicitud dirigida al Instituto Nacional
            Electoral accediendo al siguiente enlace:
          </p>
          <div className="pf-links-group">
            <PillLink
              href="https://portalanterior.ine.mx/archivos3/portal/historico/contenido/Acreditacion_y_Presenciade_Representantes_de_Partidos_Politicos_ante_casillas/"
              isMultiline={true}
            >
              Registro de Representantes ante mesas directivas de casilla
            </PillLink>
          </div>
        </PreguntaFrecuente>

        {/* Pregunta X */}
        <PreguntaFrecuente
          id="pregunta-x"
          numero="X"
          pregunta="¿Qué mecanismos de Participación Ciudadana se han ejercido en el Estado de Tlaxcala?"
        >
          <p>
            La Ley de Consulta Ciudadana para el Estado de Tlaxcala fue
            publicada en el Periódico Oficial del Gobierno del Estado de
            Tlaxcala el 26 de julio de 2006, y entró en vigor el 01 de enero de
            2007. Esta Ley regula los procesos de Iniciativa Popular, Consulta
            Popular, Plebiscito, Referéndum y Voz Ciudadana en el Cabildo.
          </p>
          <p>
            Desde la entrada en vigor y hasta la fecha{" "}
            <strong>
              no se ha ejercido alguno de los mecanismos de participación
              ciudadana que contempla la Ley.
            </strong>
          </p>
          <p>
            Te invitamos a que conozcas más a fondo los mecanismos de
            participación ciudadana que contempla nuestra ley, accediendo al
            siguiente enlace:
          </p>
          <div className="pf-links-group">
            <PillLink href="https://participacion-ciudadana.itetlax.org.mx/">
              Micrositio Participación Ciudadana
            </PillLink>
          </div>
        </PreguntaFrecuente>

        {/* Pregunta XI */}
        <PreguntaFrecuente
          id="pregunta-xi"
          numero="XI"
          pregunta="¿Cuáles son las acciones afirmativas que ha emitido el Instituto Tlaxcalteca de Elecciones?"
        >
          <p>
            Las acciones afirmativas constituyen medidas compensatorias para las
            personas en situaciones de desventaja, a fin de revertir escenarios
            de desigualdad histórica y de facto en el ejercicio de sus derechos.
          </p>
          <p>
            Preocupado por cerrar estas brechas de desigualdad histórica y
            social, y garantizar la inclusión el Instituto Tlaxcalteca de
            Elecciones ha emitido diversas acciones afirmativas para asegurar la
            participación en las elecciones de grupos históricamente vulnerados.
          </p>
          <p>
            Consulta las acciones afirmativas de este Órgano Electoral en los
            siguientes enlaces:
          </p>
          <div className="pf-links-group">
            <PillLink href="https://itetlax.org.mx/assets/pdf/acuerdos/ITE/2020/47.pdf">
              Acuerdo ITE-CG 47/2020
            </PillLink>
            <PillLink href="https://itetlax.org.mx/assets/pdf/acuerdos/ITE/2020/64.pdf">
              Acuerdo ITE-CG 64/2020
            </PillLink>
            <PillLink href="https://itetlax.org.mx/assets/pdf/acuerdos/ITE/2021/34.pdf">
              Acuerdo ITE-CG 34/2021
            </PillLink>
            <PillLink href="https://itetlax.org.mx/assets/pdf/acuerdos/ITE/2021/132.pdf">
              Acuerdo ITE-CG 132/2021
            </PillLink>
            <PillLink
              href="https://itetlax.org.mx/assets/pdf/memElect/11.pdf"
              isMultiline={true}
            >
              Memoria Electoral del Proceso Electoral Local Ordinario 2020 - 2021 y Proceso Electoral Extraordinario 2021
            </PillLink>
            <PillLink
              href="https://ite-consulta-pcd.itetlax.org.mx/"
              isMultiline={true}
            >
              Acciones Afirmativas para el Proceso Electoral Local Ordinario 2023 - 2024
            </PillLink>
          </div>
        </PreguntaFrecuente>

        {/* Pregunta XII */}
        <PreguntaFrecuente
          id="pregunta-xii"
          numero="XII"
          pregunta="¿Dónde puedo consultar la información relativa a las elecciones que se han llevado a cabo en Tlaxcala?"
        >
          <p>
            Después de cada proceso electoral el Instituto Tlaxcalteca de
            Elecciones, realiza un concentrado de información e imágenes sobre
            las actividades realizadas en los procesos electorales ordinarios o
            extraordinarios que desarrolla. A este documento se le denomina
            Memoria Electoral.
          </p>
          <p>
            Te invitamos a consultar las memorias electorales que ha elaborado
            nuestro Instituto a lo largo de diversos procesos electorales
            accediendo al siguiente enlace:
          </p>
          <div className="pf-links-group">
            <PillLink href="https://itetlax.org.mx/MemoriasElectorales">
              Memorias Electorales
            </PillLink>
          </div>
        </PreguntaFrecuente>

        {/* Pregunta XIII */}
        <PreguntaFrecuente
          id="pregunta-xiii"
          numero="XIII"
          pregunta="¿Dónde puedo consultar la información sobre las acciones que el Instituto Tlaxcalteca de Elecciones ha llevado a cabo en favor de las mujeres Tlaxcaltecas?"
        >
          <p>
            El Instituto Tlaxcalteca de Elecciones ha emitido diversas acciones
            en pro de la promoción, fomento y preservación de los derechos
            político-electorales de las mujeres, te invitamos a conocer mas en
            el siguiente enlace:
          </p>
          <div className="pf-links-group">
            <PillLink href="https://ite-genero.itetlax.org.mx/">
              ITE Género
            </PillLink>
          </div>
        </PreguntaFrecuente>
      </div>
    </>
  );
};

export default PreguntasFrecuentes;
